import { FiArrowLeft, GoLinkExternal } from '@/components/react-icons/main';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import selectors from '@/redux/selectors/selectors';
import { integrators as integratorsSelector } from '@/redux/selectors/settingsSelectors';
import routes from '@/routes';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import appsPageSlugs from '@/screens/apps/lib/appsPageSlugs.js';
import { isEmbeddedSubpage } from '@/screens/embedded/lib/embeddedSubpages.js';
import { useSelector } from 'react-redux';

const getWorkflowItems = ({ workflowId, workflowVersion = 'published', actionId, canViewLogs }) => {
  return [
    { label: 'Editor', active: !['properties', 'logs', 'settings'].includes(actionId), value: 'editor', route: `/workflows/${workflowId}/${workflowVersion}` },
    { label: 'Properties', active: actionId === 'properties', value: 'properties', route: `/workflows/${workflowId}/${workflowVersion}/properties/overview` }
  ];
};

const getJobItems = (jobId, jobSubpage) => [
  { label: 'Logs', active: jobSubpage === 'logs', value: 'logs', route: `/jobs/${jobId}/logs` },
  { label: 'Config', active: jobSubpage === 'config', value: 'config', route: `/jobs/${jobId}/config` },
  { label: 'Notifications', active: jobSubpage === 'notifications', value: 'notifications', route: `/jobs/${jobId}/notifications` },
  { label: 'Support', active: jobSubpage === 'support', value: 'support', route: `https://mindcloud.co/tickets`, rightIcon: GoLinkExternal }
];

const getAppItems = (appId, appName, version) => {
  return [{ label: appName ?? 'Overview', active: true, value: 'home', route: `/apps/${appId}/${version}` }];
};

const getIntegrationItems = (integrationId, integrationName) => {
  return [{ label: integrationName ?? 'Overview', active: true, value: 'home', route: `/embedded/${integrationId}` }];
};

const getDefaultItems = (activeRoute, section, canViewJobs, canViewWorkflows, canViewApps, canViewCredentials, canViewEmbedded, canViewTurnkeyIntegrations, isDeveloper, isAdmin, isIntegrator, hasProjects) => [
  { label: 'Home', active: activeRoute?.routeId === 'home', route: '/' },
  { label: 'Full Service', active: activeRoute?.routeId === 'fullService', route: '/full-service', condition: hasProjects },
  { label: 'My Projects', active: activeRoute?.routeId === 'monitoring' && section === 'integrator-overview', route: '/monitoring?section=integrator-overview', condition: isIntegrator },
  { label: 'Recipes', active: activeRoute?.routeId === 'recipes', route: '/recipes/popular', condition: canViewWorkflows },
  { label: 'Workflows', active: activeRoute?.routeId === 'workflows', route: '/workflows', condition: canViewWorkflows },
  { label: 'Embedded', active: activeRoute?.routeId === 'embedded', route: '/embedded', condition: canViewEmbedded },
  { label: 'Turnkey Integrations', active: activeRoute?.routeId === 'turnkeyIntegrations', route: '/turnkey-integrations', condition: canViewTurnkeyIntegrations },
  { label: 'Connections', active: activeRoute?.routeId === 'credentials', route: '/credentials', condition: canViewCredentials },
  { label: 'Apps', active: activeRoute?.routeId === 'apps', route: '/apps', condition: canViewApps },
  { label: 'Jobs', active: activeRoute?.routeId === 'jobs', route: '/jobs', condition: canViewJobs },
  { label: 'Monitoring', active: activeRoute?.routeId === 'monitoring', route: '/monitoring', condition: isDeveloper },
  { label: 'Settings', active: activeRoute?.routeId === 'userSettings', route: '/user/organization' },
  { label: 'Admin', active: activeRoute?.routeId === 'adminSettings', route: '/admin-settings/companies', condition: isAdmin }
];

const getReturnItemConfig = (workflowId, jobId, appId, integrationId, currentWorkflowIntegrationId) => {
  if (workflowId) {
    if (currentWorkflowIntegrationId) {
      return { label: 'Return to Integration', route: `/embedded/${currentWorkflowIntegrationId}` };
    }
    return { label: 'Return to Workflows', route: '/workflows' };
  }

  if (jobId) {
    return { label: 'Return to Jobs', route: '/jobs' };
  }

  if (appId) {
    return { label: 'Return to Apps', route: '/apps' };
  }

  if (integrationId) {
    return { label: 'Return to Integrations', route: '/embedded' };
  }

  return { label: 'Return to Home', route: '/' };
};

const usePlatformNav = () => {
  const activeRouteId = useSelector((state) => state.ui.activeRouteId);
  const activeRoute = routes.find((n) => n.id === activeRouteId);
  const section = useArgSelector(selectors.query, 'section');
  const enableEmbedded = useSelector((state) => selectors.activeCompany(state)?.settings?.enableEmbedded);
  const enableTurnkeyIntegrations = useSelector((state) => selectors.activeCompany(state)?.settings?.enableTurnkeyIntegrations);

  const canViewApps = useArgSelector(selectors.permission, 'app:view');
  const canViewCredentials = useArgSelector(selectors.permission, 'credential:view');
  const canViewJobs = useArgSelector(selectors.permission, 'job:view');
  const canViewLogs = useArgSelector(selectors.permission, 'log:view');
  const canViewWorkflows = useArgSelector(selectors.permission, 'workflow:view');
  // enableEmbedded alone gates the nav: connect-only embedded companies consume
  // credentials through the Universal API and have no embedded app configured.
  const canViewEmbedded = useArgSelector(selectors.permission, 'embedded:view') && !!enableEmbedded;
  const canViewTurnkeyIntegrations = enableTurnkeyIntegrations || false;
  const isDeveloper = useArgSelector(selectors.permission, 'developer:all');
  const isAdmin = useSelector(selectors.admin);
  const userId = useSelector(selectors.userId);

  // Use integrators from Redux state (fetched at app initialization in OrgStateHandler)
  const integrators = useSelector(integratorsSelector) || [];
  const isIntegrator = integrators.some((user) => (user.user_id || user.userId) === userId);

  const appId = useArgSelector(selectors.param, 'appId');
  const actionId = useArgSelector(selectors.param, 'actionId');
  const jobId = useArgSelector(selectors.param, 'jobId');
  const version = useArgSelector(selectors.param, 'version');
  const jobSubpage = useArgSelector(selectors.param, 'jobSubpage');
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const workflowVersion = useArgSelector(selectors.param, 'workflowVersion');
  const integrationIdParam = useArgSelector(selectors.param, 'integrationId');
  // Reserved embedded subpages (workflows/api/credential-exchange/mcp) ride the
  // :integrationId param. Only a real integration detail overrides the nav.
  const integrationId = isEmbeddedSubpage(integrationIdParam) ? null : integrationIdParam;
  const projects = useSelector((state) => selectors.companyIntegrationsProjects(state));
  const integrationName = useArgSelector(selectors.embeddedIntegrationById, integrationId)?.name;
  const appName = useSelector((state) => appScreenSelectors.activeApp(state)?.name);
  const currentWorkflowIntegrationId = useSelector((state) => selectors.activeWorkflow(state)?.integrationId);

  const hasProjects = (projects || []).length > 0;

  const items = [];

  const isAppEditorRoute = appId && !appsPageSlugs.has(appId);

  if (workflowId || jobId || isAppEditorRoute || integrationId) {
    const returnConfig = getReturnItemConfig(workflowId, jobId, isAppEditorRoute ? appId : null, integrationId, currentWorkflowIntegrationId);
    items.push({
      label: returnConfig.label,
      value: 'return',
      type: 'return',
      icon: FiArrowLeft,
      route: returnConfig.route
    });
  }

  if (activeRoute?.routeId === 'workflows' && workflowId) {
    items.push(...getWorkflowItems({ workflowId, workflowVersion, actionId, canViewLogs }));
  } else if (activeRoute?.routeId === 'jobs' && jobId) {
    items.push(...getJobItems(jobId, jobSubpage));
  } else if (activeRoute?.routeId === 'apps' && isAppEditorRoute) {
    items.push(...getAppItems(appId, appName, version));
  } else if (activeRoute?.routeId === 'embedded' && integrationId) {
    items.push(...getIntegrationItems(integrationId, integrationName));
  } else {
    items.push(...getDefaultItems(activeRoute, section, canViewJobs, canViewWorkflows, canViewApps, canViewCredentials, canViewEmbedded, canViewTurnkeyIntegrations, isDeveloper, isAdmin, isIntegrator, hasProjects));
  }

  return items.filter((item) => item.condition !== false);
};

export default usePlatformNav;
